import { useSnapshot } from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import type { SessionCostEvent, SessionCostHistory } from '@web/api/types'
import { Icon } from '@web/components/ui/icons'
import { fmtUsd, modelLabel } from '@web/lib/format'
import { costHighWater } from '@web/state/bridge'
import { getSessionStore } from '@web/state/session-store'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import '@web/components/session-cost.css'

/** Keep exact nanounits readable, including increases smaller than a cent. */
export function exactUsd(nanos: string): string {
  if (!/^\d+$/.test(nanos)) return '—'
  const padded = nanos.padStart(10, '0')
  const dollars = BigInt(padded.slice(0, -9)).toLocaleString('en-US')
  const fraction = padded.slice(-9).replace(/0+$/, '').padEnd(4, '0')
  return `$${dollars}.${fraction}`
}

const moneyNanos = (value: number): string =>
  BigInt(Math.round(value * 1e9)).toString()
const timeLabel = (seconds: number): string =>
  new Date(seconds * 1000).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })

function eventLabel(event: SessionCostEvent): string {
  if (event.usage?.cost_basis === 'not_reported') return 'Token usage'
  if (event.source === 'opening_balance') return 'Earlier recorded cost'
  if (event.source === 'session_opened') return 'Session started'
  return event.purpose === 'naming' ? 'Session naming' : 'Conversation'
}

function providerLabel(event: SessionCostEvent): string {
  if (!event.provider || event.provider === 'unknown') return ''
  return event.provider === 'claude'
    ? 'Claude'
    : event.provider === 'openai'
      ? 'OpenAI'
      : event.provider
}

/** Universal cost control, including draft and attached workspace sessions. */
export function SessionCost({
  sessionId,
  total = 0,
  unreported = false,
}: {
  sessionId?: string | null
  total?: number
  unreported?: boolean
}) {
  return sessionId ? (
    <BoundSessionCost
      key={sessionId}
      sessionId={sessionId}
      total={total}
      unreported={unreported}
    />
  ) : (
    <span
      className="session-cost"
      aria-label={
        unreported
          ? 'Session cost: not reported'
          : `Session cost: ${fmtUsd(total)}`
      }
    >
      <Icon name="activity" size={12} />
      {unreported ? 'Cost not reported' : fmtUsd(total)}
    </span>
  )
}

function BoundSessionCost({
  sessionId,
  total,
  unreported,
}: {
  sessionId: string
  total: number
  unreported: boolean
}) {
  const store = useMemo(() => getSessionStore(sessionId), [sessionId])
  const { bridge, acp } = useSnapshot(store)
  const [open, setOpen] = useState(false)
  const trigger = useRef<HTMLButtonElement>(null)
  const cost = costHighWater(bridge.totalCostUsd, total)
  const unknown =
    unreported ||
    (cost === 0 &&
      !acp.costs.some(({ cost }) => cost.currency === 'USD') &&
      bridge.record?.cost_status !== 'reported')
  useEffect(() => store.observeCost(total), [store, total])
  return (
    <>
      <button
        ref={trigger}
        className="session-cost session-cost-button"
        aria-label={
          unknown
            ? 'Session cost: not reported'
            : `Session cost: ${fmtUsd(cost)}`
        }
        aria-haspopup="dialog"
        title={
          unknown
            ? 'The provider has not reported a dollar charge for this session.'
            : `View session cost history · ${exactUsd(moneyNanos(cost))} USD`
        }
        onClick={() => setOpen(true)}
      >
        <Icon name="activity" size={12} />
        {unknown ? 'Cost not reported' : fmtUsd(cost)}
      </button>
      {open && (
        <CostAudit
          sessionId={sessionId}
          total={cost}
          unreported={unknown}
          eventIndex={bridge.costEventIndex}
          onTotal={store.observeCost}
          onClose={() => {
            setOpen(false)
            requestAnimationFrame(() => trigger.current?.focus())
          }}
        />
      )}
    </>
  )
}

/** Read the immutable audit without waking a session or sending provider commands. */
function CostAudit({
  sessionId,
  total,
  unreported,
  eventIndex,
  onTotal,
  onClose,
}: {
  sessionId: string
  total: number
  unreported: boolean
  eventIndex: number
  onTotal: (value: number) => void
  onClose: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const heading = useId()
  const description = useId()
  const [history, setHistory] = useState<SessionCostHistory | null>(null)
  const saved = useRef<SessionCostHistory | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const generation = useRef(0)
  const active = useRef(true)

  const load = useCallback(
    async (after = 0) => {
      const request = ++generation.current
      setBusy(true)
      setError('')
      try {
        const page = await api.sessionCosts(sessionId, after)
        if (!active.current || request !== generation.current) return
        const previous = saved.current
        // Pages overlap after live refreshes. Entries are immutable, so keep one
        // copy of each index without summing amounts in the browser.
        const indexed = new Map(
          (previous?.events ?? []).map((event) => [event.event_index, event]),
        )
        for (const event of page.events)
          if (!indexed.has(event.event_index))
            indexed.set(event.event_index, event)
        const events = [...indexed.values()].sort(
          (a, b) => a.event_index - b.event_index,
        )
        const last = events.at(-1)?.event_index ?? 0
        const latest = Math.max(
          previous?.latest_event_index ?? 0,
          page.latest_event_index,
        )
        const newerTotal =
          !previous || BigInt(page.total_nanos) >= BigInt(previous.total_nanos)
        const next = {
          ...page,
          events,
          latest_event_index: latest,
          event_count: Math.max(previous?.event_count ?? 0, page.event_count),
          total_usd: newerTotal ? page.total_usd : previous.total_usd,
          total_nanos: newerTotal ? page.total_nanos : previous.total_nanos,
          next_after: last < latest ? last : null,
        }
        saved.current = next
        setHistory(next)
        onTotal(next.total_usd)
      } catch (failure) {
        if (active.current && request === generation.current)
          setError(
            (failure as Error).message || 'Cost history could not be loaded.',
          )
      } finally {
        if (active.current && request === generation.current) setBusy(false)
      }
    },
    [sessionId, onTotal],
  )

  useEffect(() => {
    active.current = true
    const element = dialog.current
    element?.showModal()
    return () => {
      active.current = false
      generation.current += 1
      element?.close()
    }
  }, [])
  useEffect(() => {
    const current = saved.current
    void load(
      current && current.next_after === null
        ? (current.events.at(-1)?.event_index ?? 0)
        : 0,
    )
  }, [load, eventIndex, total])
  const currentTotal = costHighWater(total, history?.total_usd)
  const exactTotal =
    history && history.total_usd >= currentTotal
      ? history.total_nanos
      : moneyNanos(currentTotal)
  const events = history?.events ?? []
  const opening = events.some((event) => event.source === 'opening_balance')
  const unknown = unreported || history?.cost_status === 'not_reported'

  // Keep modal controls outside the dark status bar's descendant styling.
  return createPortal(
    <dialog
      ref={dialog}
      className="cost-audit"
      aria-labelledby={heading}
      aria-describedby={description}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onKeyDown={(event) => {
        event.stopPropagation()
        if (event.key !== 'Tab') return
        const controls = [
          ...event.currentTarget.querySelectorAll<HTMLElement>(
            'button:not(:disabled), summary, a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
          ),
        ].filter((element) => element.getClientRects().length > 0)
        if (!controls.length) return
        event.preventDefault()
        const index = controls.indexOf(document.activeElement as HTMLElement)
        controls[
          (index + (event.shiftKey ? -1 : 1) + controls.length) %
            controls.length
        ]?.focus()
      }}
    >
      <header className="cost-audit-heading">
        <div>
          <p className="label">Session activity</p>
          <h2 id={heading}>Session cost</h2>
        </div>
        <button
          autoFocus
          className="btn btn-icon"
          aria-label="Close cost history"
          onClick={onClose}
        >
          <Icon name="close" size={18} />
        </button>
      </header>
      <div className="cost-audit-body">
        <div className="cost-audit-summary">
          <div>
            <span className="cost-audit-total-label">
              {unknown ? 'Session cost' : 'Total recorded cost'}
            </span>
            <strong
              aria-label={
                unknown
                  ? 'Cost not reported'
                  : `Total recorded cost: ${exactUsd(exactTotal)}`
              }
            >
              {unknown ? (
                'Not reported'
              ) : (
                <>
                  {exactUsd(exactTotal)}
                  <small>USD</small>
                </>
              )}
            </strong>
          </div>
          <button
            className="btn btn-sm"
            disabled={busy}
            aria-label="Refresh cost history"
            onClick={() => void load()}
          >
            <Icon
              name={busy ? 'loader' : 'refresh'}
              size={13}
              className={busy ? 'spin' : ''}
            />
            Refresh
          </button>
        </div>
        <p id={description} className="cost-audit-description">
          {unknown
            ? 'The provider has not reported a dollar charge. Saved usage does not imply free usage.'
            : 'Provider-reported costs, saved across sleep and resume. New charges appear when the provider reports them.'}
        </p>
        {busy && !history && (
          <p role="status" className="cost-audit-state">
            Loading cost history…
          </p>
        )}
        {error && (
          <div className="cost-audit-error" role="alert">
            <span>{error}</span>
            <button
              className="btn btn-sm"
              disabled={busy}
              onClick={() => void load()}
            >
              Try again
            </button>
          </div>
        )}
        {history && (
          <>
            {!unknown && <CostChart events={events} />}
            {!unknown && history.total_usd === 0 && (
              <p className="cost-audit-empty">
                No provider costs have been recorded for this session.
              </p>
            )}
            <div className="cost-audit-history-heading">
              <h3>{unknown ? 'Usage history' : 'Cost history'}</h3>
              <span role="status">
                {events.length} of {history.event_count} entries loaded
              </span>
            </div>
            {opening && (
              <p className="cost-audit-note">
                Earlier recorded cost is the saved opening balance. Its original
                dates and breakdown are unavailable.
              </p>
            )}
            {events.length > 0 ? (
              <div className="cost-audit-table-wrap">
                <table className="cost-audit-table">
                  <caption className="sr-only">
                    {unknown
                      ? 'Chronological saved usage history'
                      : 'Chronological provider cost history in USD'}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Recorded</th>
                      <th scope="col">Activity</th>
                      <th scope="col">Added</th>
                      <th scope="col">Running total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((event) => (
                      <tr key={event.event_index}>
                        <td>
                          <time
                            dateTime={new Date(
                              event.recorded_at * 1000,
                            ).toISOString()}
                          >
                            {timeLabel(event.recorded_at)}
                          </time>
                          <span className="cost-audit-index">
                            Entry {event.event_index}
                          </span>
                        </td>
                        <td>
                          <strong>{eventLabel(event)}</strong>
                          {event.source === 'provider' &&
                            (providerLabel(event) || event.model) && (
                              <span className="cost-audit-source">
                                {[
                                  providerLabel(event),
                                  event.model ? modelLabel(event.model) : '',
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            )}
                          <CostDetails event={event} />
                        </td>
                        <td className="cost-audit-money">
                          {event.usage?.cost_basis === 'not_reported' ||
                          (unknown && event.source === 'session_opened')
                            ? 'Not reported'
                            : exactUsd(event.delta_nanos)}
                        </td>
                        <td className="cost-audit-money">
                          {unknown
                            ? 'Not reported'
                            : exactUsd(event.total_nanos)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="cost-audit-state">
                No cost entries are available yet.
              </p>
            )}
            {history.next_after !== null && (
              <div className="cost-audit-pagination">
                <p>
                  {unknown
                    ? 'The table shows the usage history loaded so far.'
                    : 'The chart shows the history loaded so far. The total above includes every recorded charge.'}
                </p>
                <button
                  className="btn"
                  disabled={busy}
                  onClick={() => void load(history.next_after ?? 0)}
                >
                  {busy ? 'Loading…' : 'Load more history'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </dialog>,
    document.body,
  )
}

function CostDetails({ event }: { event: SessionCostEvent }) {
  const usage = Object.entries(event.usage ?? {}).filter(
    ([name, value]) =>
      (typeof value === 'number' && Number.isFinite(value)) ||
      ([
        'report_type',
        'counter_origin',
        'currency',
        'cost_basis',
        'auth_mode',
      ].includes(name) &&
        typeof value === 'string'),
  )
  const reported =
    event.source === 'provider' &&
    (usage.length ||
      event.source_seq != null ||
      event.runner_seq != null ||
      event.observed_at != null)
  if (!reported) return null
  return (
    <details className="cost-audit-details">
      <summary>Reported details</summary>
      <dl>
        {event.model && (
          <>
            <dt>Model</dt>
            <dd>{event.model}</dd>
          </>
        )}
        {event.observed_at != null && (
          <>
            <dt>Observed</dt>
            <dd>{timeLabel(event.observed_at)}</dd>
          </>
        )}
        {usage.map(([name, value]) => (
          <div key={name}>
            <dt>
              {name.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}
            </dt>
            <dd>{String(value)}</dd>
          </div>
        ))}
        {event.source_seq != null && (
          <>
            <dt>Transcript entry</dt>
            <dd>{event.source_seq}</dd>
          </>
        )}
        {event.runner_seq != null && (
          <>
            <dt>Provider report</dt>
            <dd>{event.runner_seq}</dd>
          </>
        )}
      </dl>
    </details>
  )
}

/** The table provides the exact accessible alternative to the cumulative chart. */
function CostChart({ events }: { events: SessionCostEvent[] }) {
  if (!events.length) return null
  const first = events[0]!
  const last = events.at(-1)!
  const max = Math.max(last.total_usd, 0.01)
  const times: number[] = []
  for (const event of events)
    times.push(
      Math.max(
        event.recorded_at,
        (times.at(-1) ?? event.recorded_at - 0.001) + 0.001,
      ),
    )
  const start = times[0]!
  const duration = Math.max(times.at(-1)! - start, 0.001)
  const x = (index: number) => 66 + ((times[index]! - start) / duration) * 536
  const y = (value: number) => 148 - (value / max) * 116
  const path = events.reduce(
    (value, event, index) =>
      `${value}${index ? ` H${x(index)} V${y(event.total_usd)}` : `M66 ${y(event.total_usd)}`}`,
    '',
  )
  return (
    <figure className="cost-audit-chart">
      <svg
        viewBox="0 0 630 184"
        role="img"
        aria-label={`Cumulative recorded cost for ${events.length} loaded entries, ending at ${exactUsd(last.total_nanos)}. Exact values appear in the cost history table.`}
      >
        {[0, 0.5, 1].map((fraction) => (
          <g key={fraction}>
            <line
              className="cost-chart-grid"
              x1="66"
              x2="604"
              y1={y(max * fraction)}
              y2={y(max * fraction)}
            />
            <text x="56" y={y(max * fraction) + 4} textAnchor="end">
              {fmtUsd(max * fraction)}
            </text>
          </g>
        ))}
        <path className="cost-chart-line" d={path} />
        {events.map((event, index) => (
          <circle
            key={event.event_index}
            className="cost-chart-point"
            cx={x(index)}
            cy={y(event.total_usd)}
            r={events.length > 100 ? 2 : 3}
          >
            <title>
              {timeLabel(event.recorded_at)} · {eventLabel(event)} ·{' '}
              {exactUsd(event.total_nanos)}
            </title>
          </circle>
        ))}
        <text x="66" y="174">
          {new Date(first.recorded_at * 1000).toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </text>
        {events.length > 1 && (
          <text x="604" y="174" textAnchor="end">
            {new Date(last.recorded_at * 1000).toLocaleTimeString(undefined, {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </text>
        )}
      </svg>
      <figcaption>Cumulative recorded cost · USD</figcaption>
    </figure>
  )
}
